/**
 * 檔案用途區塊
 * @module WalletConnectI18nHook
 * @purpose 提供 WalletConnect 流程的語系字典動態載入、翻譯鍵查找與 Hook 介面，並在載入失敗時回退到預設語系。
 * @external https://innotech.atlassian.net/browse/IN-111889
 * @external https://innotech.atlassian.net/browse/IN-113493
 */
import { useEffect, useState, useMemo } from 'react';
import { LocaleDash } from '@shared/enums';
import { WalletConnectI18nDictionary, WalletConnectI18nKey } from '@locale/keys';

/**
 * 宣告內容用途說明與單號關聯
 * @description Promise 快取表，記錄各語系翻譯檔案載入中的 Promise，避免重複發送相同動態載入。
 * @purpose 降低同語系重複 import 的開銷並共用載入結果。
 * @external https://innotech.atlassian.net/browse/IN-113493
 */
const importLocalePromiseMap: Partial<Record<LocaleDash, Promise<{ default: WalletConnectI18nDictionary }>>> = {};

/**
 * 宣告內容用途說明與單號關聯
 * @description 預設回退語系設定。
 * @purpose 當指定語系缺失或載入失敗時作為替代來源。
 * @external https://innotech.atlassian.net/browse/IN-113493
 */
const DEFAULT_LOCALE: LocaleDash = LocaleDash.en_US;

/**
 * 宣告內容用途說明與單號關聯
 * @description 依語系動態載入翻譯模組，並在首次載入後寫入 Promise 快取；若失敗則回退預設語系。
 * @purpose 統一翻譯檔案載入流程並提供可重用的非同步入口。
 * @external https://innotech.atlassian.net/browse/IN-113493
 */
function importLocale(locale: LocaleDash): Promise<{ default: WalletConnectI18nDictionary }> {
  // 檢查是否已有快取的 Promise
  const cachedPromise = importLocalePromiseMap[locale];
  if (cachedPromise) {
    return cachedPromise;
  }

  // 動態 import 翻譯檔案（locale 位於 src/assets/locale）
  // 注意：檔案名稱必須與 LocaleDash 的值一致（例如：'en-us.ts'）
  // 這裡使用相對路徑（hooks → walletconnect → modules → src → assets）
  const promise = import(`../../../assets/locale/${locale}`).catch(() => {
    // 如果載入失敗（例如檔案不存在），fallback 到預設語系
    return import(`../../../assets/locale/${DEFAULT_LOCALE}`);
  }) as Promise<{ default: WalletConnectI18nDictionary }>;

  // 快取 Promise
  importLocalePromiseMap[locale] = promise;
  return promise;
}

/**
 * 宣告內容用途說明與單號關聯
 * @description 依點號分隔的巢狀 key 從字典中解析翻譯值，若解析不到字串則回傳 fallback 或原 key。
 * @purpose 將翻譯取值邏輯集中處理，避免呼叫端重複實作巢狀路徑走訪。
 * @external https://innotech.atlassian.net/browse/IN-113493
 */
function getTranslationValue(
  dictionary: WalletConnectI18nDictionary,
  key: WalletConnectI18nKey,
  fallback?: string,
): string {
  const keyStr = String(key);
  const keys: string[] = keyStr.split('.');

  // 使用 reduce 遍歷巢狀結構
  const value = keys.reduce<unknown>((acc: unknown, k: string) => {
    if (acc && typeof acc === 'object' && k in acc) {
      return (acc as Record<string, unknown>)[k];
    }
    return undefined;
  }, dictionary);

  if (typeof value === 'string') {
    return value;
  }

  return fallback ?? keyStr;
}

/**
 * 宣告內容用途說明與單號關聯
 * @description WalletConnect 專用 i18n Hook，管理語系安全值、字典載入狀態、錯誤狀態與翻譯函數輸出。
 * @purpose 讓呼叫端以一致介面取得 t/isLoading/error，簡化頁面對翻譯載入生命週期的處理。
 * @external https://innotech.atlassian.net/browse/IN-111889
 */
export function useWalletConnectI18n(locale?: LocaleDash) {
  const safeLocale: LocaleDash = locale ?? DEFAULT_LOCALE;

  // useState 泛型依 @locale/keys 解析；path alias 正確時型別無誤，避免 strict 規則誤報
  const [dictionary, setDictionary] = useState<WalletConnectI18nDictionary | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  // 載入翻譯檔案
  useEffect(() => {
    let isMounted = true;

    setIsLoading(true);
    setError(null);

    importLocale(safeLocale)
      .then((module: { default: WalletConnectI18nDictionary }) => {
        if (isMounted) {
          setDictionary(module.default);
          setIsLoading(false);
        }
      })
      .catch((err) => {
        if (isMounted) {
          setError(err instanceof Error ? err : new Error(String(err)));
          setIsLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [safeLocale]);

  // 翻譯函數
  const t = useMemo(
    () =>
      (key: WalletConnectI18nKey, fallback?: string): string => {
        if (!dictionary) {
          return fallback ?? String(key);
        }
        return getTranslationValue(dictionary, key, fallback);
      },
    [dictionary],
  );

  return { t, isLoading, error };
}

/**
 * === llm 分析紀錄區 ===
 * @llm-review-submitted-at 2026-06-14T07:18:32.399Z
 * @llm-review-model gpt-5.3-codex
 * @llm-review-note Reformatted annotations into the required three-section layout, added compliant @module/@purpose/@external in top block, added declaration-level blocks using only mapped Jira tickets, omitted @external where no ticket mapping was required, and appended the bottom llm review record block. Runtime logic unchanged.
 */
